import { useCallback, useRef, useEffect } from "react";
import { Node, Edge } from "reactflow";
import { message } from "antd";
import { ClipboardData } from "../utils/types";
import { getNodeChildren, getNextNodeId, deduplicateEdges, syncNodeIdCounterWithExistingNodes } from "../utils/utils";

interface UseCopyPasteOperationsProps {
  nodes: Node[];
  edges: Edge[];
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => void;
  clipboard: ClipboardData;
  setClipboard: (clipboard: ClipboardData) => void;
  saveToHistory: (actionType: "add" | "delete" | "paste") => void;
  isIdentifierInUse: (identifier: string, excludeNodeId?: string) => boolean;
  updateNodeHandlers: (currentEdges?: Edge[]) => void;
  forceRepositionAllNodes?: () => void;
  getNodeDescendants: (nodeId: string, allNodes: Node[], allEdges: Edge[]) => string[];
  repositionChildrenCentered: (parentNodeId: string, allNodes: Node[], allEdges: Edge[], alreadyPositionedNodes?: Set<string>) => Node[];
}

export const useCopyPasteOperations = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  clipboard,
  setClipboard,
  saveToHistory,
  isIdentifierInUse,
  updateNodeHandlers,
  forceRepositionAllNodes,
  getNodeDescendants,
  repositionChildrenCentered,
}: UseCopyPasteOperationsProps) => {

  // Track timeout IDs to prevent memory leaks
  const timeoutIdsRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const isMountedRef = useRef(true);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      timeoutIdsRef.current.forEach(clearTimeout);
      timeoutIdsRef.current.clear();
    };
  }, []);

  // Helper function to create managed timeout
  const createManagedTimeout = useCallback((callback: () => void, delay: number) => {
    const timeoutId = setTimeout(() => {
      timeoutIdsRef.current.delete(timeoutId);
      if (isMountedRef.current) {
        callback();
      }
    }, delay);
    timeoutIdsRef.current.add(timeoutId);
    return timeoutId;
  }, []);

  // Helper function to generate unique identifier with "_copy" suffix
  const generateUniqueIdentifier = useCallback((originalIdentifier: string): string => {
    if (!originalIdentifier) return "";
    
    let newIdentifier = `${originalIdentifier}_copy`;
    let counter = 1;
    
    // Keep incrementing until we find a unique identifier
    while (isIdentifierInUse(newIdentifier)) {
      newIdentifier = `${originalIdentifier}_copy${counter}`;
      counter++;
    }
    
    return newIdentifier;
  }, [isIdentifierInUse]);

  // Clear clipboard and remove highlights
  const clearClipboard = useCallback(() => {
    // Remove highlights from nodes AND clear ReactFlow selection state
    if (clipboard.highlighted.length > 0) {
      setNodes(currentNodes => currentNodes.map(node => {
        return {
          ...node,
          selected: false, // Clear ReactFlow's internal selection state
          data: {
            ...node.data,
            isHighlighted: false
          }
        };
      }));
    }
    
    setClipboard({
      type: null,
      sourceId: null,
      sourceNode: null,
      sourceBranch: null,
      sourceEdges: null,
      highlighted: []
    });
  }, [clipboard.highlighted.length, setNodes, setClipboard]);

  // getNodeDescendants is now passed as a parameter

  // Copy a single node
  const copyNode = useCallback((nodeId: string) => {
    // Find the node
    const nodeToCopy = nodes.find(node => node.id === nodeId);
    if (!nodeToCopy) return;
    
    // Copy node to clipboard
    setClipboard({
      type: "node",
      sourceId: nodeId,
      sourceNode: {...nodeToCopy},
      sourceBranch: null,
      sourceEdges: null,
      highlighted: [nodeId]
    });
    
    // Just update the data without changing positions
    const newNodes = nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            isHighlighted: true
          }
        };
      } else {
        return {
          ...node,
          data: {
            ...node.data,
            isHighlighted: false
          }
        };
      }
    });
    
    setNodes(newNodes);
    message.success("Node copied to clipboard");
  }, [nodes, setNodes, setClipboard]);

  // Copy a node and all its descendants (branch)
  const copyBranch = useCallback((nodeId: string) => {
    // Find all descendants
    const descendantIds = getNodeDescendants(nodeId, nodes, edges);
    if (!descendantIds.length && getNodeChildren(nodeId, edges).length === 0) {
      // If no descendants, fallback to single node copy
      copyNode(nodeId);
      message.info("No branch to copy - copied single node instead");
      return;
    }
    
    // Include the root node
    const branchNodeIds = [nodeId, ...descendantIds];
    
    // Get all nodes in the branch
    const branchNodes = nodes.filter(node => branchNodeIds.includes(node.id));
    
    // Get all edges in the branch
    const branchEdges = edges.filter(edge => 
      branchNodeIds.includes(edge.source) && branchNodeIds.includes(edge.target)
    );
    
    setClipboard({
      type: "branch",
      sourceId: nodeId,
      sourceNode: null,
      sourceBranch: branchNodes,
      sourceEdges: branchEdges,
      highlighted: branchNodeIds
    });
    
    // Just update the data without changing positions
    const newNodes = nodes.map(node => {
      return {
        ...node,
        data: {
          ...node.data,
          isHighlighted: branchNodeIds.includes(node.id)
        }
      };
    });
    
    setNodes(newNodes);
    message.success(`Branch with ${branchNodes.length} nodes copied to clipboard`);
  }, [nodes, edges, setNodes, setClipboard, getNodeDescendants, copyNode]);

  // Paste a single node
  const pasteNode = useCallback((targetNodeId: string) => {
    if (!clipboard.sourceNode) {
      message.error("No node copied to clipboard");
      return;
    }

    // Check if target node exists
    const targetNode = nodes.find(node => node.id === targetNodeId);
    if (!targetNode) {
      message.error("Target node not found");
      return;
    }

    // Prevent pasting USER or DECISION nodes onto DECISION nodes
    if (targetNode.data.entityType === "DECISION" && 
        (clipboard.sourceNode.data.entityType === "USER" || clipboard.sourceNode.data.entityType === "DECISION")) {
      const sourceType = clipboard.sourceNode.data.entityType.toLowerCase();
      message.error(`Cannot paste ${sourceType} node onto a decision node. Decision nodes can only connect to bot nodes.`);
      return;
    }

    // Generate unique identifier if the original has one
    let newIdentifier = "";
    if (clipboard.sourceNode.data.identifier) {
      newIdentifier = generateUniqueIdentifier(clipboard.sourceNode.data.identifier);
    }

    // Save current state to history
    saveToHistory("paste");

    // Create new node with new ID and unique identifier
    const newNodeId = getNextNodeId().toString();
    const newNode: Node = {
      ...clipboard.sourceNode,
      id: newNodeId,
      position: {
        x: targetNode.position.x + 200,
        y: targetNode.position.y + 150
      },
      data: {
        ...clipboard.sourceNode.data,
        identifier: newIdentifier, // Use the unique identifier
        hasChildren: false,
        isDescendantOfDestination: false,
        globalRegistry: {},
        onPlaceholderClick: () => {},
        onAddNode: () => {},
        onEditPlaceholder: () => {},
        onEditNode: () => {},
        onDeleteNode: () => {},
        onPreviewConversation: () => {},
        onContextMenu: () => {}
      }
    };

    // Create new edge
    const edgeId = `e${targetNodeId}-${newNodeId}`;
    const newEdge: Edge = {
      id: edgeId,
      source: targetNodeId,
      target: newNodeId,
      type: "custom"
    };

    // Add node and edge
    setNodes(nds => [...nds, newNode]);
    setEdges(eds => {
      // Check if edge already exists
      const existingEdge = eds.find(edge => edge.id === edgeId);
      if (existingEdge) {
        // console.log("🚫 Paste: Edge already exists, skipping:", edgeId);
        return eds;
      }
      
      const updatedEdges = [...eds, newEdge];
      
      // After edge is added, reposition children for any parent node (except DECISION nodes)
      const parentNode = nodes.find(n => n.id === targetNodeId);
      if (parentNode && parentNode.data.entityType !== "DECISION") {
        console.log(`[DEBUG] Centering: Paste edge added, will reposition children for ${parentNode.data.entityType} parent ${targetNodeId}`);
        setTimeout(() => {
          setNodes(currentNodes => {
            console.log(`[DEBUG] Centering: Running repositionChildrenCentered for parent ${targetNodeId} after paste`);
            return repositionChildrenCentered(targetNodeId, currentNodes, updatedEdges);
          });
        }, 100);
      }
      
      // console.log("✅ Paste: Creating new edge:", edgeId);
      return updatedEdges;
    });

    // Sync node ID counter to prevent conflicts with future node additions
    createManagedTimeout(() => {
      setNodes(currentNodes => {
        syncNodeIdCounterWithExistingNodes(currentNodes);
        return currentNodes;
      });
    }, 50);

    // Update node handlers and trigger repositioning with longer delay
    createManagedTimeout(() => {
      updateNodeHandlers();
      createManagedTimeout(() => {
        forceRepositionAllNodes?.();
      }, 50);
    }, 300);

    message.success("Node pasted successfully");
  }, [clipboard.sourceNode, nodes, edges, generateUniqueIdentifier, saveToHistory, setNodes, setEdges, updateNodeHandlers, forceRepositionAllNodes, createManagedTimeout]);

  // Paste an entire branch
  const pasteBranch = useCallback((targetNodeId: string) => {
    if (!clipboard.sourceBranch || !clipboard.sourceEdges) {
      message.error("No branch copied to clipboard");
      return;
    }

    // Check if target node exists
    const targetNode = nodes.find(node => node.id === targetNodeId);
    if (!targetNode) {
      message.error("Target node not found");
      return;
    }

    // Get the root node of the branch
    const rootNode = clipboard.sourceBranch[0]; // Use first node from branch instead of sourceNode

    // Prevent pasting branches with USER or DECISION root nodes onto DECISION nodes
    if (targetNode.data.entityType === "DECISION" && 
        (rootNode.data.entityType === "USER" || rootNode.data.entityType === "DECISION")) {
      const rootType = rootNode.data.entityType.toLowerCase();
      message.error(`Cannot paste branch with ${rootType} root node onto a decision node. Decision nodes can only connect to bot nodes.`);
      return;
    }

    // Save current state to history
    saveToHistory("paste");

    // Create mapping from old IDs to new IDs and old identifiers to new identifiers
    const idMapping: Record<string, string> = {};
    const identifierMapping: Record<string, string> = {};
    
    // console.log("🔄 Starting ID mapping for", clipboard.sourceBranch.length, "nodes");
    clipboard.sourceBranch.forEach((node: Node) => {
      const newId = getNextNodeId().toString();
      idMapping[node.id] = newId;
      // console.log(`🔄 ID Mapping: ${node.id} -> ${newId} (identifier: ${node.data.identifier || "none"})`);
      if (node.data.identifier) {
        const newIdentifier = generateUniqueIdentifier(node.data.identifier);
        identifierMapping[node.data.identifier] = newIdentifier;
        // console.log(`🔄 Identifier Mapping: ${node.data.identifier} -> ${newIdentifier}`);
      }
    });
    // console.log("🔄 Final ID mapping:", idMapping);
    // console.log("🔄 Final identifier mapping:", identifierMapping);

    // Create new nodes with updated IDs, positions, and unique identifiers
    const newNodes: Node[] = clipboard.sourceBranch.map((node: Node) => {
      // Get the new identifier for this node
      const newIdentifier = node.data.identifier ? identifierMapping[node.data.identifier] : "";
      
      // Handle decision node path updates
      let updatedDecisionData = node.data.decisionData;
      if (node.data.entityType === "DECISION" && updatedDecisionData) {
        updatedDecisionData = { ...updatedDecisionData };
        
        // Update truePath if it references a node in the copied branch
        if (updatedDecisionData.truePath && identifierMapping[updatedDecisionData.truePath]) {
          updatedDecisionData.truePath = identifierMapping[updatedDecisionData.truePath];
        }
        
        // Update falsePath if it references a node in the copied branch
        if (updatedDecisionData.falsePath && identifierMapping[updatedDecisionData.falsePath]) {
          updatedDecisionData.falsePath = identifierMapping[updatedDecisionData.falsePath];
        }
      }

      return {
        ...node,
        id: idMapping[node.id],
        position: {
          x: node.position.x + (targetNode.position.x - rootNode.position.x) + 200,
          y: node.position.y + (targetNode.position.y - rootNode.position.y) + 150
        },
        data: {
          ...node.data,
          identifier: newIdentifier, // Use the unique identifier
          decisionData: updatedDecisionData, // Use updated decision data
          globalRegistry: {},
          onPlaceholderClick: () => {},
          onAddNode: () => {},
          onEditPlaceholder: () => {},
          onEditNode: () => {},
          onDeleteNode: () => {},
          onPreviewConversation: () => {},
          onContextMenu: () => {}
        }
      };
    });

    // Create new edges with updated IDs
    const newEdges: Edge[] = clipboard.sourceEdges.map((edge: Edge) => {
      const newEdgeId = `e${idMapping[edge.source]}-${idMapping[edge.target]}`;
      const newEdge = {
        ...edge,
        id: newEdgeId,
        source: idMapping[edge.source],
        target: idMapping[edge.target]
      };
      // console.log(`🔄 Edge Mapping: ${edge.id} (${edge.source}->${edge.target}) -> ${newEdgeId} (${newEdge.source}->${newEdge.target})`);
      return newEdge;
    });

    // Add edge from target to root of pasted branch
    const rootToTargetEdgeId = `e${targetNodeId}-${idMapping[rootNode.id]}`;
    const rootToTargetEdge: Edge = {
      id: rootToTargetEdgeId,
      source: targetNodeId,
      target: idMapping[rootNode.id],
      type: "custom"
    };

    // Add all nodes and edges
    // console.log("🚀 Paste branch: Adding", newNodes.length, "nodes");
    // console.log("🚀 Paste branch: Identifier mappings:", identifierMapping);
    
    setNodes(nds => [...nds, ...newNodes]);
    setEdges(eds => {
      // Check for duplicates in newEdges and rootToTargetEdge
      const allNewEdges = [...newEdges, rootToTargetEdge];
      // console.log("🚀 Paste branch: Current edges count:", eds.length);
      // console.log("🚀 Paste branch: Attempting to add", allNewEdges.length, "edges");
      // console.log("🚀 Paste branch: New edges to add:", allNewEdges.map(e => e.id));
      
      const validNewEdges = allNewEdges.filter(newEdge => {
        const existingEdge = eds.find(edge => edge.id === newEdge.id);
        if (existingEdge) {
          // console.log("🚫 Paste branch: Edge already exists, skipping:", newEdge.id);
          return false;
        }
        // console.log("✅ Paste branch: Creating new edge:", newEdge.id);
        return true;
      });
      
      // console.log("🚀 Paste branch: Adding", validNewEdges.length, "valid edges");
      const finalEdges = deduplicateEdges([...eds, ...validNewEdges]);
      // console.log("🚀 Paste branch: Final edges count after deduplication:", finalEdges.length);
      return finalEdges;
    });

    // Sync node ID counter to prevent conflicts with future node additions
    createManagedTimeout(() => {
      setNodes(currentNodes => {
        syncNodeIdCounterWithExistingNodes(currentNodes);
        return currentNodes;
      });
    }, 100);

    // Update node handlers and trigger repositioning with longer delay for branches
    createManagedTimeout(() => {
      updateNodeHandlers();
      createManagedTimeout(() => {
        forceRepositionAllNodes?.();
      }, 100);
    }, 500);

    message.success(`Branch with ${newNodes.length} nodes pasted successfully`);
  }, [clipboard.sourceNode, clipboard.sourceBranch, clipboard.sourceEdges, nodes, edges, generateUniqueIdentifier, saveToHistory, setNodes, setEdges, updateNodeHandlers, forceRepositionAllNodes, createManagedTimeout]);

  return {
    copyNode,
    copyBranch,
    pasteNode,
    pasteBranch,
    clearClipboard,
    getNodeDescendants
  };
}; 